import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

const colorSchemes = [
  {
    id: 'tron_orange',
    name: 'Tron Orange (Default)',
    primary: '#FF6B00',
    secondary: '#000000',
    accent: '#FFFFFF'
  },
  {
    id: 'cyber_blue',
    name: 'Cyber Blue',
    primary: '#00D4FF',
    secondary: '#000B1A',
    accent: '#FFFFFF'
  },
  {
    id: 'neon_green',
    name: 'Neon Green',
    primary: '#39FF14',
    secondary: '#001A00',
    accent: '#FFFFFF'
  },
  {
    id: 'purple_haze',
    name: 'Purple Haze',
    primary: '#9D00FF',
    secondary: '#0A000D',
    accent: '#FFFFFF'
  },
  {
    id: 'hot_pink',
    name: 'Hot Pink',
    primary: '#FF006E',
    secondary: '#1A0008',
    accent: '#FFFFFF'
  },
  {
    id: 'electric_yellow',
    name: 'Electric Yellow',
    primary: '#FFD60A',
    secondary: '#1A1400',
    accent: '#000000'
  }
];

export const applyColorScheme = (schemeId) => {
  const scheme = colorSchemes.find(s => s.id === schemeId);
  if (scheme) {
    document.documentElement.style.setProperty('--color-primary', scheme.primary);
    document.documentElement.style.setProperty('--color-secondary', scheme.secondary);
    document.documentElement.style.setProperty('--color-accent', scheme.accent);
  }
};

export default function ColorSchemeSelector() {
  const [currentScheme, setCurrentScheme] = useState('tron_orange');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchCurrentScheme();
  }, []);

  const fetchCurrentScheme = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentScheme(response.data.color_scheme);
      applyColorScheme(response.data.color_scheme);
    } catch (error) {
      console.error('Failed to fetch color scheme:', error);
    }
  };

  const handleSchemeChange = async (schemeId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/preferences`,
        { color_scheme: schemeId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCurrentScheme(schemeId);
      applyColorScheme(schemeId);
      toast.success('Color scheme updated!');
    } catch (error) {
      toast.error('Failed to update color scheme');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="retro-panel">
      <h3 className="text-2xl font-bold mb-4 glow-text" style={{ color: 'var(--color-primary, #FF6B00)' }}>
        [COLOR SCHEME]
      </h3>
      
      <div className="space-y-3">
        {colorSchemes.map(scheme => (
          <button
            key={scheme.id}
            onClick={() => handleSchemeChange(scheme.id)}
            disabled={loading}
            className={`w-full p-4 rounded border-2 transition-all ${
              currentScheme === scheme.id
                ? 'border-current scale-105'
                : 'border-gray-600 hover:border-gray-400'
            }`}
            style={{
              background: `linear-gradient(135deg, ${scheme.secondary} 0%, ${scheme.primary}20 100%)`,
              borderColor: currentScheme === scheme.id ? scheme.primary : undefined
            }}
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-lg font-bold" style={{ color: scheme.accent }}>
                  {scheme.name}
                </div>
                <div className="flex gap-2 mt-2">
                  <div
                    className="w-8 h-8 rounded border"
                    style={{ backgroundColor: scheme.primary }}
                    title="Primary"
                  />
                  <div
                    className="w-8 h-8 rounded border"
                    style={{ backgroundColor: scheme.secondary }}
                    title="Secondary"
                  />
                  <div
                    className="w-8 h-8 rounded border"
                    style={{ backgroundColor: scheme.accent }}
                    title="Accent"
                  />
                </div>
              </div>
              {currentScheme === scheme.id && (
                <div className="text-2xl" style={{ color: scheme.primary }}>
                  ✓
                </div>
              )}
            </div>
          </button>
        ))}
      </div>

      <div className="mt-4 text-sm" style={{ color: '#FF6B00' }}>
        * Changes apply immediately to your account
      </div>
    </div>
  );
}
